import React, { useContext, useEffect, useState } from "react";
import AppRouter from "./Router";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  IconButton,
  ListItemIcon,
  Button,
} from "@mui/material";
import { ReactKeycloakProvider, useKeycloak } from "@react-keycloak/web";
import keycloak from "./keycloak";
import { Logout } from "@mui/icons-material";
import { RoleContext, RoleProvider } from "./lib/contexts/RoleContext";
import RouteSwitchButton from "./components/RouteSwitchButton";
import AccessDenied from "./pages/AccessDenied";
import { checkAllowAccess } from "./lib/utils/access";
import { LicenseInfo } from "@mui/x-license";
import { getInitials } from "./lib/utils/common";

// Set the MUI X DataGrid Pro license key
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || "");

const App: React.FC = () => {
  const { keycloak, initialized } = useKeycloak();
  const [userName, setUserName] = useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { role } = useContext(RoleContext);

  useEffect(() => {
    const evaluateAccess = async () => {
      if (!initialized) {
        return;
      }
      if (keycloak?.authenticated) {
        const user = keycloak.tokenParsed;
        const email = user?.email || "";

        // Persist token + email first so API calls (including the access
        // check below) can attach the Authorization and Email headers.
        localStorage.setItem("autoGraderToken", keycloak.token || "");
        localStorage.setItem("user", email);
        const firstName = user?.given_name || "";
        const lastName = user?.family_name || "";
        const fullName = `${firstName} ${lastName}`.trim();
        // Fall back to the email when Keycloak has no name, so the account
        // menu (and its Logout button) still renders.
        setUserName(fullName || email);

        // @upgrad.com users are always allowed (no DB check). Everyone else
        // must be present in the backend allow_access collection.
        const allowed = await checkAllowAccess(email, role);
        setIsAuthorized(allowed);
        if (!allowed) {
          setUserName("");
        }
      } else {
        setUserName("");
      }
      setLoading(false);
    };

    evaluateAccess();
  }, [initialized, keycloak, role]);

  useEffect(() => {
    if (initialized && !keycloak?.authenticated) {
      keycloak?.login();
    }
  }, [initialized, keycloak]);
  if (loading) {
    return <p>Loading...</p>;
  }

  if (!keycloak?.authenticated) {
    return <p>Redirecting to Keycloak...</p>;
  }

  if (!isAuthorized) {
    return <AccessDenied onLogout={() => keycloak.logout()} />;
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Box width={"150px"} position={"relative"}>
              <img
                src="https://www.upgrad.com/_ww3-next/_next/static/media/upgrad-header-logo.325f003e.svg"
                alt="upGrad logo"
              />
              <Typography
                ml={2}
                fontSize={14}
                sx={{ position: "absolute", bottom: -10, right: 10 }}
                fontWeight={700}
              >
                Autograder Tool
              </Typography>
            </Box>
          </Box>
          {/* This check is now safe because we've already confirmed authentication and authorization */}
          {userName && (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                gap={4}
                mr={2}
              >
                <RouteSwitchButton />
                {role === "learn" && (
                  <Button
                    onClick={() =>
                      (window.location.href = "/learn/downloadQuizByCohort")
                    }
                    variant="outlined"
                    sx={{ mr: 1 }}
                  >
                    Download Quiz By Cohort
                  </Button>
                )}
                <Button
                  onClick={() =>
                    (window.location.href = `/${role}/performanceDashboard`)
                  }
                  variant="outlined"
                >
                  Performance Dashboard
                </Button>
              </Box>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {getInitials(userName)}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      overflow: "visible",
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      mt: 1.5,
                      "& .MuiAvatar-root": {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      "&::before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleClose}>
                  <Avatar /> {userName}
                </MenuItem>
                <MenuItem onClick={() => keycloak?.logout()}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth={false}>
        <RoleProvider>
          <AppRouter />
        </RoleProvider>
      </Container>
    </>
  );
};

const keycloakConfig: any = {
  authClient: keycloak,
  onEvent: (event: any) => {
    if (event === "onAuthenticated") {
      console.log("User authenticated");
    } else if (event === "onTokenExpired") {
      console.log("Token expired");
    }
  },
};

const AppWithKeycloakProvider: React.FC = () => {
  return (
    <ReactKeycloakProvider {...keycloakConfig}>
      <App />
    </ReactKeycloakProvider>
  );
};

export default AppWithKeycloakProvider;
